import { Pool } from "../utils/Pool";
import { Utils } from "../utils/Utils";
import { MathUtils3D } from "./MathUtils3D";
export class Matrix {
    static equals(a, b) {
        return MathUtils3D.nearEqual(a.a, b.a)
            && MathUtils3D.nearEqual(a.b, b.b)
            && MathUtils3D.nearEqual(a.c, b.c)
            && MathUtils3D.nearEqual(a.d, b.d)
            && MathUtils3D.nearEqual(a.tx, b.tx)
            && MathUtils3D.nearEqual(a.ty, b.ty);
    }
    static extractTransformInfo(matrix) {
        let { a, b, c, d, tx, ty } = matrix;
        let x = tx;
        let y = ty;
        const det = a * d - b * c;
        const sign = det < 0 ? -1 : 1;
        let scaleX = Math.sqrt(a * a + b * b);
        let scaleY = sign * Math.sqrt(c * c + d * d);
        let rotation = Utils.toAngle(Math.atan2(b, a));
        let skewX = 0;
        let skewY = 0;
        if (scaleX !== 0 && scaleY !== 0) {
            const norm_a = a / scaleX;
            const norm_b = b / scaleX;
            const norm_c = c / scaleY;
            const norm_d = d / scaleY;
            const cos = norm_a;
            const sin = norm_b;
            const derot_c = norm_c * cos + norm_d * sin;
            const derot_d = -norm_c * sin + norm_d * cos;
            skewX = Utils.toAngle(Math.atan2(derot_c, derot_d));
            const dotProduct = a * c + b * d;
            const len1 = Math.sqrt(a * a + b * b);
            const len2 = Math.sqrt(c * c + d * d);
            if (len1 !== 0 && len2 !== 0) {
                const cosTheta = dotProduct / (len1 * len2);
                const clampedCosTheta = Math.max(-1, Math.min(1, cosTheta));
                skewY = Utils.toAngle(Math.PI / 2 - Math.acos(clampedCosTheta));
            }
        }
        return {
            x, y,
            scaleX, scaleY,
            rotation,
            skewX, skewY
        };
    }
    constructor(a = 1, b = 0, c = 0, d = 1, tx = 0, ty = 0, nums = 0) {
        this._bTransform = false;
        if (Matrix._createFun != null) {
            return Matrix._createFun(a, b, c, d, tx, ty, nums);
        }
        this.a = a;
        this.b = b;
        this.c = c;
        this.d = d;
        this.tx = tx;
        this.ty = ty;
        this._checkTransform();
    }
    identity() {
        this.a = this.d = 1;
        this.b = this.tx = this.ty = this.c = 0;
        this._bTransform = false;
        return this;
    }
    _checkTransform() {
        return this._bTransform = (this.a !== 1 || this.b !== 0 || this.c !== 0 || this.d !== 1);
    }
    setTranslate(x, y) {
        this.tx = x;
        this.ty = y;
        return this;
    }
    translate(x, y) {
        this.tx += x;
        this.ty += y;
        return this;
    }
    scale(x, y) {
        this.a *= x;
        this.d *= y;
        this.c *= x;
        this.b *= y;
        this.tx *= x;
        this.ty *= y;
        this._bTransform = true;
        return this;
    }
    rotate(angle) {
        var cos = Math.cos(angle);
        var sin = Math.sin(angle);
        var a1 = this.a;
        var c1 = this.c;
        var tx1 = this.tx;
        this.a = a1 * cos - this.b * sin;
        this.b = a1 * sin + this.b * cos;
        this.c = c1 * cos - this.d * sin;
        this.d = c1 * sin + this.d * cos;
        this.tx = tx1 * cos - this.ty * sin;
        this.ty = tx1 * sin + this.ty * cos;
        this._bTransform = true;
        return this;
    }
    skew(x, y) {
        var sinx = Math.sin(x);
        var cosx = Math.cos(x);
        var siny = Math.sin(y);
        var cosy = Math.cos(y);
        var a = this.a;
        var c = this.c;
        var tx = this.tx;
        this.a = cosy * a + sinx * this.b;
        this.b = siny * a + cosx * this.b;
        this.c = cosy * c + sinx * this.d;
        this.d = siny * c + cosx * this.d;
        this.tx = cosy * tx + sinx * this.ty;
        this.ty = siny * tx + cosx * this.ty;
        this._bTransform = true;
        return this;
    }
    invertTransformPoint(out) {
        var a1 = this.a;
        var b1 = this.b;
        var c1 = this.c;
        var d1 = this.d;
        var tx1 = this.tx;
        var n = a1 * d1 - b1 * c1;
        var a2 = d1 / n;
        var b2 = -b1 / n;
        var c2 = -c1 / n;
        var d2 = a1 / n;
        var tx2 = (c1 * this.ty - d1 * tx1) / n;
        var ty2 = -(a1 * this.ty - b1 * tx1) / n;
        return out.setTo(a2 * out.x + c2 * out.y + tx2, b2 * out.x + d2 * out.y + ty2);
    }
    transformPoint(out) {
        return out.setTo(this.a * out.x + this.c * out.y + this.tx, this.b * out.x + this.d * out.y + this.ty);
    }
    transformPointN(out) {
        return out.setTo(this.a * out.x + this.c * out.y, this.b * out.x + this.d * out.y);
    }
    getScaleX() {
        return this.b === 0 ? this.a : Math.sqrt(this.a * this.a + this.b * this.b);
    }
    getScaleY() {
        return this.c === 0 ? this.d : Math.sqrt(this.c * this.c + this.d * this.d);
    }
    invert() {
        var a1 = this.a;
        var b1 = this.b;
        var c1 = this.c;
        var d1 = this.d;
        var tx1 = this.tx;
        var n = a1 * d1 - b1 * c1;
        this.a = d1 / n;
        this.b = -b1 / n;
        this.c = -c1 / n;
        this.d = a1 / n;
        this.tx = (c1 * this.ty - d1 * tx1) / n;
        this.ty = -(a1 * this.ty - b1 * tx1) / n;
        return this;
    }
    setTo(a, b, c, d, tx, ty) {
        this.a = a, this.b = b, this.c = c, this.d = d, this.tx = tx, this.ty = ty;
        return this;
    }
    concat(matrix) {
        var a = this.a;
        var c = this.c;
        var tx = this.tx;
        this.a = a * matrix.a + this.b * matrix.c;
        this.b = a * matrix.b + this.b * matrix.d;
        this.c = c * matrix.a + this.d * matrix.c;
        this.d = c * matrix.b + this.d * matrix.d;
        this.tx = tx * matrix.a + this.ty * matrix.c + matrix.tx;
        this.ty = tx * matrix.b + this.ty * matrix.d + matrix.ty;
        return this;
    }
    static mul(m1, m2, out) {
        var aa = m1.a, ab = m1.b, ac = m1.c, ad = m1.d, atx = m1.tx, aty = m1.ty;
        var ba = m2.a, bb = m2.b, bc = m2.c, bd = m2.d, btx = m2.tx, bty = m2.ty;
        if (bb !== 0 || bc !== 0) {
            out.a = aa * ba + ab * bc;
            out.b = aa * bb + ab * bd;
            out.c = ac * ba + ad * bc;
            out.d = ac * bb + ad * bd;
            out.tx = ba * atx + bc * aty + btx;
            out.ty = bb * atx + bd * aty + bty;
        }
        else {
            out.a = aa * ba;
            out.b = ab * bd;
            out.c = ac * ba;
            out.d = ad * bd;
            out.tx = ba * atx + btx;
            out.ty = bd * aty + bty;
        }
        return out;
    }
    static mul16(m1, m2, out) {
        var aa = m1.a, ab = m1.b, ac = m1.c, ad = m1.d, atx = m1.tx, aty = m1.ty;
        var ba = m2.a, bb = m2.b, bc = m2.c, bd = m2.d, btx = m2.tx, bty = m2.ty;
        if (bb !== 0 || bc !== 0) {
            out[0] = aa * ba + ab * bc;
            out[1] = aa * bb + ab * bd;
            out[4] = ac * ba + ad * bc;
            out[5] = ac * bb + ad * bd;
            out[12] = ba * atx + bc * aty + btx;
            out[13] = bb * atx + bd * aty + bty;
        }
        else {
            out[0] = aa * ba;
            out[1] = ab * bd;
            out[4] = ac * ba;
            out[5] = ad * bd;
            out[12] = ba * atx + btx;
            out[13] = bd * aty + bty;
        }
        return out;
    }
    scaleEx(x, y) {
        var ba = this.a, bb = this.b, bc = this.c, bd = this.d;
        if (bb !== 0 || bc !== 0) {
            this.a = x * ba;
            this.b = x * bb;
            this.c = y * bc;
            this.d = y * bd;
        }
        else {
            this.a = x * ba;
            this.b = 0 * bd;
            this.c = 0 * ba;
            this.d = y * bd;
        }
        this._bTransform = true;
    }
    rotateEx(angle) {
        var cos = Math.cos(angle);
        var sin = Math.sin(angle);
        var ba = this.a, bb = this.b, bc = this.c, bd = this.d;
        if (bb !== 0 || bc !== 0) {
            this.a = cos * ba + sin * bc;
            this.b = cos * bb + sin * bd;
            this.c = -sin * ba + cos * bc;
            this.d = -sin * bb + cos * bd;
        }
        else {
            this.a = cos * ba;
            this.b = sin * bd;
            this.c = -sin * ba;
            this.d = cos * bd;
        }
        this._bTransform = true;
    }
    clone() {
        var dec = new Matrix();
        dec.a = this.a;
        dec.b = this.b;
        dec.c = this.c;
        dec.d = this.d;
        dec.tx = this.tx;
        dec.ty = this.ty;
        dec._bTransform = this._bTransform;
        return dec;
    }
    cloneTo(dec) {
        dec.a = this.a;
        dec.b = this.b;
        dec.c = this.c;
        dec.d = this.d;
        dec.tx = this.tx;
        dec.ty = this.ty;
        dec._bTransform = this._bTransform;
        return dec;
    }
    copyTo(dec) {
        dec.a = this.a;
        dec.b = this.b;
        dec.c = this.c;
        dec.d = this.d;
        dec.tx = this.tx;
        dec.ty = this.ty;
        dec._bTransform = this._bTransform;
        return dec;
    }
    setMatrix(x, y, sx, sy, rotation, skewX, skewY, pivotx, pivoty) {
        rotation = Utils.toRadian(rotation);
        skewX = Utils.toRadian(skewX);
        skewY = Utils.toRadian(skewY);
        const cosr = Math.cos(rotation);
        const sinr = Math.sin(rotation);
        const coskx = Math.cos(skewX);
        const sinkx = Math.sin(skewX);
        const cosky = Math.cos(skewY);
        const sinky = Math.sin(skewY);
        this.a = (cosr * cosky - sinr * sinky) * sx;
        this.b = (sinr * cosky + cosr * sinky) * sx;
        this.c = (cosr * sinkx - sinr * coskx) * sy;
        this.d = (sinr * sinkx + cosr * coskx) * sy;
        this.tx = x - this.a * pivotx - this.c * pivoty;
        this.ty = y - this.b * pivotx - this.d * pivoty;
        this._checkTransform();
        return this;
    }
    toString() {
        return this.a + "," + this.b + "," + this.c + "," + this.d + "," + this.tx + "," + this.ty;
    }
    destroy() {
        this.recover();
    }
    recover() {
        Pool.recover("Matrix", this.identity());
    }
    static create() {
        return Pool.getItemByClass("Matrix", Matrix);
    }
}
Matrix.EMPTY = new Matrix();
Matrix.TEMP = new Matrix();
Matrix._createFun = null;
